import React, { useState } from 'react';
import { Calculator } from 'lucide-react';
import { formatInr, formatSignedPercent } from '../../utils/gmpBoard';

const toNumber = (text) => {
  const value = parseFloat(text);
  return Number.isFinite(value) ? value : 0;
};

export const GmpProfitCalculator = ({ rows, loading }) => {
  const [selectedId, setSelectedId] = useState(rows[0]?.id || '');
  const selected = rows.find((row) => row.id === selectedId) || rows[0];

  const [gmpInput, setGmpInput] = useState(String(selected?.gmp ?? ''));
  const [lotInput, setLotInput] = useState(String(selected?.lotSize ?? ''));

  if (!selected) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 sm:p-5">
        <h3 className="flex items-center gap-2 text-sm font-extrabold text-slate-900">
          <Calculator className="w-4 h-4 text-[#008080]" />
          Live GMP Profit Calc
        </h3>
        <p className="text-xs text-slate-500 mt-2">
          {loading
            ? 'Loading live GMP quotes…'
            : 'No open or upcoming IPO has a GMP quote with a price and lot size right now.'}
        </p>
      </div>
    );
  }

  const handleSelect = (id) => {
    const next = rows.find((row) => row.id === id);
    setSelectedId(id);
    if (next) {
      setGmpInput(String(next.gmp));
      setLotInput(String(next.lotSize));
    }
  };

  const gmp = toNumber(gmpInput);
  const lot = toNumber(lotInput);
  const gain = gmp * lot;
  const investment = selected.price * lot;
  const roi = investment ? (gain / investment) * 100 : 0;
  const isLoss = gain < 0;

  const inputClass =
    'w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-slate-800 focus:outline-none focus:border-[#008080] focus:ring-1 focus:ring-[#008080] transition-colors';

  return (
    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 sm:p-5">
      <div className="flex items-center justify-between gap-2 mb-4">
        <h3 className="flex items-center gap-2 text-sm font-extrabold text-slate-900">
          <Calculator className="w-4 h-4 text-[#008080]" />
          Live GMP Profit Calc
        </h3>
        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-teal-50 text-[#008080] border border-teal-200">
          Per Lot
        </span>
      </div>

      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5" htmlFor="gmp-calc-ipo">
        Select Active IPO
      </label>
      <select
        id="gmp-calc-ipo"
        value={selected.id}
        onChange={(e) => handleSelect(e.target.value)}
        className={`${inputClass} font-medium truncate`}
      >
        {rows.map((row) => (
          <option key={row.id} value={row.id}>
            {row.name} ({formatInr(row.gmp)} GMP / {row.lotSize.toLocaleString('en-IN')} lot)
          </option>
        ))}
      </select>

      <div className="grid grid-cols-2 gap-3 mt-3">
        <div>
          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5" htmlFor="gmp-calc-gmp">
            GMP (₹)
          </label>
          <input
            id="gmp-calc-gmp"
            type="number"
            inputMode="decimal"
            value={gmpInput}
            onChange={(e) => setGmpInput(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5" htmlFor="gmp-calc-lot">
            Lot Size
          </label>
          <input
            id="gmp-calc-lot"
            type="number"
            inputMode="numeric"
            min="0"
            value={lotInput}
            onChange={(e) => setLotInput(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      <div
        className={`mt-4 rounded-xl p-4 text-white bg-gradient-to-br ${
          isLoss ? 'from-rose-600 to-rose-700' : 'from-emerald-600 to-teal-700'
        }`}
      >
        <div className="text-[11px] font-semibold uppercase tracking-wider text-white/80">
          Estimated Listing {isLoss ? 'Loss' : 'Gain'} / Lot
        </div>
        <div className="text-2xl font-extrabold tracking-tight mt-0.5">
          {isLoss ? '−' : ''}
          {formatInr(Math.abs(gain))}
        </div>
        <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-white/20 text-[11px] text-white/90">
          <span>Investment: {formatInr(investment)}</span>
          <span className="font-bold">ROI {formatSignedPercent(roi, 1)}</span>
        </div>
      </div>
      <p className="text-[10px] text-slate-400 mt-2">
        At the upper price band of {formatInr(selected.price)}. Excludes taxes and charges.
      </p>
    </div>
  );
};

export default GmpProfitCalculator;
